/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Grid,
  InputAdornment,
  makeStyles,
  TextField,
  Link as MuiLink,
  Typography,
  Popover,
  CircularProgress,
} from '@material-ui/core';
import ButtonComponent from 'components/ButtonComponent';
import i18n from 'i18n';
import ApplicationState from 'store/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import styles from './styles';
import LockIcon from '@material-ui/icons/Lock';
import EmailIcon from '@material-ui/icons/Email';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import ReplayIcon from '@material-ui/icons/Replay';
import { history } from 'store';
import {
  validatePinRequest,
  validateWithEmailRequest,
} from 'modules/auth/actions';
import { Link } from 'react-router-dom';
import { ConfigState } from 'modules/config/interfaces';

const useStyles = makeStyles(styles);

interface Props {
  isFictitiousEmail: boolean;
  userEmail?: string;
  isLoading?: boolean;
  config: ConfigState;
  validatePinRequest: (props) => void;
  validateWithEmailRequest: (props) => void;
}

const DeviceValidationForm: React.FC<Props> = ({
  isFictitiousEmail,
  userEmail,
  isLoading,
  config,
  validatePinRequest,
  validateWithEmailRequest,
}) => {
  const classes = useStyles();
  const [error, setError] = React.useState('');
  const [step, setStep] = React.useState<'validate' | 'choose' | 'pin'>(
    'validate'
  );
  const [authMethod, setAuthMethod] = React.useState<
    'email_pin' | 'adm_pin' | null
  >(null);
  const [pin, setPin] = React.useState('');
  const [isValidating, setIsValidating] = React.useState(true);
  const [resendTimer, setResendTimer] = React.useState(60);
  const [canResend, setCanResend] = React.useState(false);
  const resendIntervalRef = React.useRef<ReturnType<typeof setInterval>>();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const openPopover = Boolean(anchorEl);
  const handleHelpClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleHelpClose = () => {
    setAnchorEl(null);
  };

  const validateDevice = async () => {
    await new Promise(r => setTimeout(r, 1500));
    return false;
  };

  const startResendCountdown = () => {
    if (resendIntervalRef.current) {
      clearInterval(resendIntervalRef.current);
    }

    setCanResend(false);
    setResendTimer(60);

    resendIntervalRef.current = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          if (resendIntervalRef.current) {
            clearInterval(resendIntervalRef.current);
          }
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendEmailPin = () => {
    setAuthMethod('email_pin');
    setStep('pin');
    startResendCountdown();

    validateWithEmailRequest({
      validationType: 'email_pin',
    });
  };

  const handleStartValidation = async () => {
    try {
      const valid = await validateDevice();
      if (valid) {
        history.push('/home');
      } else {
        setStep('choose');
      }
    } catch (e) {
      setError(i18n.t('REGISTRATION_ERROR'));
    } finally {
      setIsValidating(false);
    }
  };

  React.useEffect(() => {
    handleStartValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendAdminPin = async () => {
    setAuthMethod('adm_pin');
    setStep('pin');
  };

  const handleValidatePin = async () => {
    validatePinRequest({
      validationType: authMethod,
      pin,
      onSuccess: (data: any) => {
        const { device_validation_code } = data;
        const expirationDate = new Date();
        expirationDate.setDate(expirationDate.getDate() + 90);

        localStorage.setItem(
          'device_validation_info',
          JSON.stringify({
            code: device_validation_code,
            expiresAt: expirationDate.toISOString(),
          })
        );

        history.push('/validating');
      },
      onError: (errorCode?: string) => {
        if (errorCode === 'MAXIMUM_PIN_INVALID_RETRIES') {
          history.push('/');
        } else {
          setError(i18n.t(errorCode || 'INVALID_PIN'));
        }
      },
    });
  };

  React.useEffect(() => {
    return () => {
      if (resendIntervalRef.current) {
        clearInterval(resendIntervalRef.current);
      }
    };
  }, []);

  if (isValidating || isLoading) {
    return (
      <Grid container justifyContent="center" alignItems="center">
        <CircularProgress />
      </Grid>
    );
  }

  if (step === 'choose') {
    return (
      <Grid container alignItems="center">
        <Grid item container xs={12} className={classes.spacingContainer}>
          <Typography variant="h3" className={classes.titleLogin}>
            <LockIcon className={classes.lockIcon} />
            {i18n.t('DEVICE_VALIDATION_KEY')}
          </Typography>

          <Grid item xs={12}>
            <Typography variant="body1" className={classes.subtitleLogin}>
              {i18n.t('DEVICE_NOT_VALIDATED_MESSAGE')}
            </Typography>
          </Grid>
        </Grid>

        <Grid container>
          {!isFictitiousEmail && (
            <Grid item container>
              <ButtonComponent
                variant="contained"
                color="primary"
                startIcon={<EmailIcon />}
                onClick={handleSendEmailPin}
              >
                {i18n.t('RECEIVE_TOKEN_EMAIL_KEY')}
              </ButtonComponent>
              <Typography variant="h6" className={classes.emailInfo}>
                {i18n.t('SEND_EMAIL_TO_KEY', {
                  email: userEmail,
                })}
              </Typography>
            </Grid>
          )}

          {!isFictitiousEmail && (
            <Grid item container justifyContent="center">
              <Typography variant="body1" className={classes.orText}>
                {i18n.t('OR_KEY')}
              </Typography>
            </Grid>
          )}
          <Grid item container>
            <ButtonComponent
              variant="contained"
              color="secondary"
              style={{ color: 'white' }}
              startIcon={<VerifiedUserIcon />}
              onClick={handleSendAdminPin}
            >
              {i18n.t('USE_ADMIN_TOKEN_KEY')}
            </ButtonComponent>
          </Grid>
        </Grid>
        <Grid container justifyContent="space-between" item xs={12}>
          <Grid item className={classes.backContainer} xs={6}>
            <Typography
              variant="button"
              component={Link}
              to="/"
              style={{
                color: config?.backgroundColor
                  ? config?.backgroundColor
                  : '#0064af',
              }}
            >
              {i18n.t('BACK_KEY')}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  if (step === 'pin') {
    return (
      <Grid container alignItems="center">
        <Grid item container xs={12} className={classes.spacingContainer}>
          <Grid item xs={12} className={classes.login}>
            <Typography variant="h3" className={classes.titleLogin}>
              {i18n.t('ENTER_RECEIVED_PIN_KEY')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {authMethod && (
              <Typography variant="body2" style={{ marginBottom: 8 }}>
                {authMethod === 'adm_pin'
                  ? i18n.t('AUTH_METHOD_ADMIN_SELECTED_KEY')
                  : i18n.t('AUTH_METHOD_EMAIL_SELECTED_KEY')}
              </Typography>
            )}
          </Grid>
        </Grid>

        <TextField
          label="Token"
          variant="outlined"
          type={'text'}
          autoFocus
          value={pin}
          onChange={e => setPin(e.target.value)}
          inputProps={{
            maxLength: 6,
            inputMode: 'numeric',
            pattern: '[0-9]*',
            style: {
              fontSize: 28,
              letterSpacing: '0.5em',
              textAlign: 'center',
            },
          }}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon color="action" />
              </InputAdornment>
            ),
          }}
          fullWidth
        />

        {error && (
          <Typography color="error" className={classes.pinError}>
            {error}
          </Typography>
        )}
        {authMethod === 'email_pin' && (
          <Grid container style={{ marginTop: 8 }}>
            <MuiLink
              component="button"
              variant="body2"
              onClick={handleSendEmailPin}
              className={classes.muiLink}
              disabled={!canResend}
              style={{
                color: !canResend ? '#9a9a9a' : '#0064af',
                cursor: !canResend ? 'default' : 'pointer',
              }}
            >
              {canResend
                ? i18n.t('RESEND_EMAIL_TOKEN_KEY')
                : `${i18n.t('RESEND_EMAIL_AVAILABLE_IN_KEY')} ${resendTimer}s`}
            </MuiLink>
          </Grid>
        )}

        <Grid
          container
          justifyContent="center"
          className={classes.validateButtonGrid}
        >
          <Grid item xs={12} className={classes.validateButton}>
            <ButtonComponent
              variant="contained"
              color="primary"
              onClick={handleValidatePin}
              fullWidth
              disabled={isLoading || pin.length < 6}
            >
              {i18n.t('VALIDATE_KEY')}
            </ButtonComponent>
          </Grid>
          <Grid item xs={12}>
            <ButtonComponent
              variant="outlined"
              color="default"
              size="small"
              onClick={() => setStep('choose')}
              startIcon={<ReplayIcon />}
              fullWidth
            >
              {i18n.t('SWITCH_METHOD_KEY')}
            </ButtonComponent>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <MuiLink
            component="button"
            variant="body2"
            onClick={handleHelpClick}
            className={classes.muiLink}
            style={{
              color: '#0064af',
            }}
          >
            {i18n.t(
              authMethod === 'adm_pin'
                ? 'DIDNT_RECEIVE_ADMIN_TOKEN_KEY'
                : 'DIDNT_RECEIVE_EMAIL_TOKEN_KEY'
            )}
          </MuiLink>

          <Popover
            id="didnt-receive-popover"
            open={openPopover}
            anchorEl={anchorEl}
            onClose={handleHelpClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <Typography className={classes.contactAdmin}>
              {i18n.t(
                authMethod === 'adm_pin'
                  ? 'CONTACT_ADMIN_FOR_ADMIN_TOKEN_KEY'
                  : 'CONTACT_ADMIN_FOR_EMAIL_TOKEN_KEY'
              )}
            </Typography>
          </Popover>
        </Grid>
      </Grid>
    );
  }

  return null;
};

const mapStateToProps = ({
  auth: { isLoading, isAuthenticate, currentEmail },
  config,
}: ApplicationState) => ({
  isLoading,
  isFictitiousEmail: isAuthenticate?.user_has_fictitious_email,
  userEmail: currentEmail,
  config,
});

export default connect(mapStateToProps, {
  validatePinRequest,
  validateWithEmailRequest,
})(DeviceValidationForm);
